import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, Download, Loader2, Search, Lock } from "lucide-react";
import { AuditReports } from "@/components/AuditReports";
import { toast } from "sonner";
import { PageHeader, Panel } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { api, formatError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATS = [["", "All categories"], ["letters", "Letters"], ["team", "Team & ownership"], ["branding", "Branding & reply-to"], ["api", "API"], ["batches", "Batches"], ["reports", "Reports & exports"], ["security", "Security"]];
const EMPTY = { q: "", category: "", date_from: "", date_to: "", flagged: false };
const PAGE = 100;

const Filters = ({ f, setF, onApply, onExport, busy }) => (
  <form onSubmit={(e) => { e.preventDefault(); onApply(); }} className="flex flex-wrap items-end gap-3" data-testid="audit-filters">
    <div className="relative flex-1 min-w-[220px]">
      <Search size={14} className="absolute left-3 top-3.5 text-slate-400" />
      <Input data-testid="audit-search-input" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Letter ref (PC-…), email or event" className="h-10 pl-9" />
    </div>
    <select data-testid="audit-category-select" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm">
      {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
    <input data-testid="audit-from-input" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm" />
    <input data-testid="audit-to-input" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm" />
    <label className="inline-flex items-center gap-2 h-10 text-sm font-medium cursor-pointer">
      <input data-testid="audit-flagged-only-checkbox" type="checkbox" checked={f.flagged} onChange={(e) => setF({ ...f, flagged: e.target.checked })} className="accent-red-600 h-4 w-4" /> Flagged only</label>
    <button data-testid="audit-apply-btn" className="h-10 px-5 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-sm font-semibold hover:bg-emerald-600 transition-colors">Apply</button>
    <button type="button" data-testid="audit-export-btn" onClick={onExport} disabled={busy} className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-slate-300 dark:border-slate-700 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-60">
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export CSV</button>
  </form>
);

const Row = ({ r }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr data-testid={`audit-row-${r.seq}`} onClick={() => setOpen(!open)} className="border-b border-slate-100 dark:border-slate-800 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40">
        <td className="px-3 py-2.5 font-mono text-xs whitespace-nowrap">{fmtDate(r.timestamp)}</td>
        <td className="px-3 py-2.5 font-mono text-xs font-semibold">{r.event}
          {r.flags?.map((f) => <span key={f.rule} title={f.reason} data-testid={`audit-flag-${r.seq}-${f.rule}`} className="ml-2 inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 px-2 py-0.5 text-[10px] font-sans font-semibold"><AlertTriangle size={10} />{f.label}</span>)}
        </td>
        <td className="px-3 py-2.5 text-xs capitalize">{r.category}</td>
        <td className="px-3 py-2.5 text-xs">{r.letter_ref ? <Link to={`/app/mail/${r.mail_id}`} onClick={(e) => e.stopPropagation()} className="font-mono underline">{r.letter_ref}</Link> : "—"}</td>
        <td className="px-3 py-2.5 text-xs">{r.actor} <span className="text-slate-500 capitalize">· {r.actor_role}</span></td>
        <td className="px-3 py-2.5 font-mono text-[11px] text-slate-500">#{r.seq}</td>
      </tr>
      {open && (
        <tr className="bg-slate-50 dark:bg-slate-900/60"><td colSpan={6} className="px-3 py-3">
          {r.flags?.map((f) => <p key={f.rule} className="text-xs text-red-700 dark:text-red-300 mb-2"><strong>{f.label}:</strong> {f.reason}</p>)}
          <pre data-testid={`audit-row-detail-${r.seq}`} className="text-[11px] font-mono whitespace-pre-wrap break-all">{JSON.stringify(r.data, null, 2)}</pre>
          <p className="text-[11px] text-slate-500 mt-2 font-mono break-all">IP {r.ip || "—"} · hash {r.hash}</p>
        </td></tr>
      )}
    </>
  );
};

export default function AuditTrail() {
  const { user } = useAuth();
  const allowed = user?.enterprise && (user.org?.perms || []).includes("members:manage");
  const [params] = useSearchParams();
  const initial = { ...EMPTY, flagged: params.get("flagged") === "1" };
  const [f, setF] = useState(initial);
  const [applied, setApplied] = useState(initial);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const fetchPage = (filters, offset) => api.get("/org/audit", { params: { ...filters, limit: PAGE, offset } }).then((r) => r.data);
  useEffect(() => {
    if (!allowed) return;
    setD(null);
    fetchPage(applied, 0).then(setD).catch((e) => toast.error(formatError(e)));
  }, [applied, allowed]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!allowed) return <div data-testid="audit-locked" className="max-w-xl"><PageHeader eyebrow="Enterprise" title="Audit trail" /><Panel><p className="flex gap-2 text-sm"><Lock size={16} className="text-emerald-600" /> The audit trail viewer is available to organisation Owners and Admins.</p></Panel></div>;
  const more = async () => {
    const next = await fetchPage(applied, d.rows.length);
    setD({ ...d, rows: [...d.rows, ...next.rows] });
  };
  const exportCsv = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/org/audit.csv", { params: applied, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      Object.assign(document.createElement("a"), { href: url, download: "PostalCloud-audit-trail.csv" }).click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="audit-page" className="space-y-6 min-w-0">
      <PageHeader eyebrow={user.org?.name} title="Audit trail" />
      <Panel className="min-w-0">
        <Filters f={f} setF={setF} onApply={() => setApplied({ ...f })} onExport={exportCsv} busy={busy} />
        {d?.flagged_total > 0 && (
          <div data-testid="audit-flag-banner" className="mt-4 rounded-lg border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/40 p-3 text-sm text-red-800 dark:text-red-200">
            <p className="flex items-center gap-2 font-semibold"><AlertTriangle size={15} /> {d.flagged_total} flagged entr{d.flagged_total === 1 ? "y" : "ies"} need review</p>
            <p className="text-xs mt-1">{Object.entries(d.flag_summary).map(([l, n]) => `${l} (${n})`).join(" · ")}</p>
            {!applied.flagged && <button data-testid="audit-show-flagged-btn" onClick={() => { const n = { ...f, flagged: true }; setF(n); setApplied(n); }} className="text-xs underline mt-1">Show flagged only</button>}
          </div>
        )}
        <p className="text-xs text-slate-500 mt-3">Every entry is part of PostalCloud's tamper-evident, hash-chained audit trail and can't be edited or deleted. Export applies the current filters and is itself recorded.</p>
      </Panel>
      <AuditReports highlight={params.get("report")} />
      {!d ? <Loader2 className="animate-spin text-emerald-600" /> : (
        <Panel title={`${d.total} entries`} className="min-w-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200 dark:border-slate-800">
                {["When (UTC)", "Event", "Category", "Letter", "By", "Seq"].map((h) => <th key={h} className="px-3 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>{d.rows.map((r) => <Row key={r.seq} r={r} />)}</tbody>
            </table>
          </div>
          {!d.rows.length && <p data-testid="audit-empty" className="text-sm text-slate-500 p-6 text-center">No entries match these filters.</p>}
          {d.rows.length < d.total && <button data-testid="audit-load-more" onClick={more} className="mt-4 text-sm underline">Load more ({d.total - d.rows.length} remaining)</button>}
        </Panel>
      )}
    </div>
  );
}
